import { DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Observable, defer, merge, of, shareReplay, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { AuthService } from '../../auth/auth.service';
import { StaticApplicationId } from '../../Constants/static-application-id.enum';
import { ModuleName, ModuleVariant, ModuleVariantPair } from './module-availability.types';

/** Action Plan legacy/revamp pair (legacy = `Planos de Ação`, revamp = `Planos de Ação - Novo`). */
const ACTION_PLAN_PAIR: ModuleVariantPair = {
  legacyId: StaticApplicationId.actionPlan,
  revampId: StaticApplicationId.actionPlanRevamp,
};

/** Defect Tag legacy/revamp pair (legacy = `Etiquetas`, revamp = `Etiquetas - Novo`). */
const DEFECT_TAG_PAIR: ModuleVariantPair = {
  legacyId: StaticApplicationId.defectTagLegacy,
  revampId: StaticApplicationId.defectTag,
};

/** CIL legacy/revamp pair (legacy = `LIL`, revamp = `CIL`, `Modules.Revamp.Cil.ApplicationId`). */
const CIL_PAIR: ModuleVariantPair = {
  legacyId: StaticApplicationId.lil,
  revampId: StaticApplicationId.lilRevamp,
};

/**
 * Resolves which modules are active for the current tenant from
 * `GET /Modules/names` and exposes, per logical module, whether the REVAMP
 * (new) or LEGACY version should be used.
 *
 * Rule: when both the legacy and revamp applications are active, the legacy
 * variant wins.
 *
 * The module list is fetched once per session (in-flight requests are shared
 * via `shareReplay(1)`) and cleared on sign-in/sign-out so the next read
 * refetches for the new tenant. State is exposed both as signals (for
 * templates/components) and as observables (for RxJS pipelines such as
 * `NavigationService`).
 */
@Injectable({ providedIn: 'root' })
export class ModuleAvailabilityService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _appConfig = inject(AppEnvironmentConfig);
  private readonly _authService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);

  /** Raw `/Modules/names` payload; `null` until the first load resolves. */
  private readonly _modules = signal<ModuleName[] | null>(null);
  readonly modules: Signal<ModuleName[] | null> = this._modules.asReadonly();

  /** Shared in-flight request, so concurrent callers trigger a single GET. */
  private _modules$: Observable<ModuleName[]> | null = null;

  /** Action Plan variant as a derived signal (reflects the latest loaded state). */
  readonly actionPlanVariant: Signal<ModuleVariant> = computed(() => this._resolve(this._modules(), ACTION_PLAN_PAIR));

  /** Action Plan variant as an observable; subscribing triggers the load if needed. */
  readonly actionPlanVariant$: Observable<ModuleVariant> = this.resolveVariant$(ACTION_PLAN_PAIR);

  /** Defect Tag variant as a derived signal (reflects the latest loaded state). */
  readonly defectTagVariant: Signal<ModuleVariant> = computed(() => this._resolve(this._modules(), DEFECT_TAG_PAIR));

  /** Defect Tag variant as an observable; subscribing triggers the load if needed. */
  readonly defectTagVariant$: Observable<ModuleVariant> = this.resolveVariant$(DEFECT_TAG_PAIR);

  /** CIL variant as a derived signal (reflects the latest loaded state). */
  readonly cilVariant: Signal<ModuleVariant> = computed(() => this._resolve(this._modules(), CIL_PAIR));

  /** CIL variant as an observable; subscribing triggers the load if needed. */
  readonly cilVariant$: Observable<ModuleVariant> = this.resolveVariant$(CIL_PAIR);

  constructor() {
    merge(this._authService.signIn$, this._authService.signOut$)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.reset());
  }

  /**
   * Ensures the module list is loaded (fetching once) and emits it. Concurrent
   * subscribers share the same request; a failed request is not cached.
   */
  load(): Observable<ModuleName[]> {
    const cached = this._modules();
    if (cached != null) return of(cached);

    if (!this._modules$) {
      this._modules$ = this._httpClient
        .get<ModuleName[]>(`${this._appConfig.APIs.apiUrlAdministration}/Modules/names`)
        .pipe(
          map((modules) => modules ?? []),
          tap((modules) => this._modules.set(modules)),
          shareReplay(1),
          catchError((error) => {
            this._modules$ = null;
            return throwError(() => error);
          }),
        );
    }

    return this._modules$;
  }

  /**
   * Resolves the variant for a legacy/revamp pair, loading the module list on
   * subscription when it has not been fetched yet.
   */
  resolveVariant$(pair: ModuleVariantPair): Observable<ModuleVariant> {
    return defer(() => this.load()).pipe(map((modules) => this._resolve(modules, pair)));
  }

  /**
   * Resolves the variant synchronously from the already-loaded list. Returns a
   * "neither active" variant when the list has not been loaded yet.
   */
  resolveVariant(pair: ModuleVariantPair): ModuleVariant {
    return this._resolve(this._modules(), pair);
  }

  /** Clears the cached module list so the next read refetches. */
  reset(): void {
    this._modules.set(null);
    this._modules$ = null;
  }

  private _resolve(modules: ModuleName[] | null, pair: ModuleVariantPair): ModuleVariant {
    const legacyActive = this._isActive(modules, pair.legacyId);
    const revampActive = this._isActive(modules, pair.revampId);

    let activeApplicationId: string | null = null;
    if (legacyActive) activeApplicationId = pair.legacyId;
    else if (revampActive) activeApplicationId = pair.revampId;

    return {
      // Legacy wins when both are active.
      isLegacy: legacyActive,
      isRevamp: revampActive && !legacyActive,
      activeApplicationId,
    };
  }

  private _isActive(modules: ModuleName[] | null, applicationId: string): boolean {
    if (!modules?.length || !applicationId) return false;
    const target = applicationId.toUpperCase();
    return modules.some((module) => module.applicationId?.toUpperCase() === target && module.isActive);
  }
}
